import { debounce } from "lodash";
import BoxFilters from "../BoxFilters/BoxFilters";
import { boxCategory } from "../../api/modules/b2droppie.class";
import BackdropLoader from "../BackdropLoader/BackdropLoader";
import BoxListCarousel from "../BoxListCarousel/BoxListCarousel";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";

const UnBoxing = () => {
  const PER_PAGE = 10;
  const observerRef = useRef(null);
  const isInitialMount = useRef(true);
  const abortControllerRef = useRef(null);
  const [boxData, setBoxData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showOverlay, setShowOverlay] = useState(false);
  const [sortFilter, setSortFilter] = useState({
    filter_by: "box_price",
    order_by: "desc",
  });
  const [boxCategories, setBoxCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    hasMore: true,
  });

  // Main fetch function
  const fetchBoxes = useCallback(async (params, isLoadMore = false) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setLoading(true);

    try {
      const response = await boxCategory(params.category, {
        page: params.page,
        per_page: PER_PAGE,
        search: params.searchQuery,
        subCategories: params.subCategories,
        filter_by: params.sortFilter.filter_by,
        order_by: params.sortFilter.order_by,
      });

      const paginationData = response?.data?.boxes;
      const newBoxData = paginationData?.data || [];

      setBoxData((prevData) => {
        return isLoadMore ? [...prevData, ...newBoxData] : newBoxData;
      });

      setPagination({
        currentPage: paginationData.current_page,
        lastPage: paginationData.last_page,
        hasMore: paginationData.current_page < paginationData.last_page,
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching boxes:", error);
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, []);

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((params) => {
        fetchBoxes(params, false);
      }, 500),
    [fetchBoxes],
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchBoxes(
        {
          category,
          searchQuery,
          subCategories: selectedSubCategories,
          sortFilter,
          page: 1,
        },
        false,
      );
      return;
    }

    debouncedSearch.cancel();

    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchBoxes(
      {
        category,
        searchQuery,
        subCategories: selectedSubCategories,
        sortFilter,
        page: 1,
      },
      false,
    );
  }, [category, selectedSubCategories, sortFilter, fetchBoxes]);

  // Handle search with debounce
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }

    debouncedSearch.cancel();
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    debouncedSearch({
      category,
      searchQuery,
      subCategories: selectedSubCategories,
      sortFilter,
      page: 1,
    });
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    const element = observerRef.current;
    if (!element || !pagination.hasMore || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && pagination.hasMore) {
          const nextPage = pagination.currentPage + 1;

          fetchBoxes(
            {
              category,
              searchQuery,
              subCategories: selectedSubCategories,
              sortFilter,
              page: nextPage,
            },
            true,
          );
        }
      },
      {
        rootMargin: "100px",
        threshold: 0.1,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [
    pagination.hasMore,
    pagination.currentPage,
    loading,
    category,
    searchQuery,
    selectedSubCategories,
    sortFilter,
    fetchBoxes,
  ]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedSearch]);

  return (
    <>
      <BoxFilters
        category={category}
        sortFilter={sortFilter}
        setCategory={setCategory}
        searchQuery={searchQuery}
        setSortFilter={setSortFilter}
        subCategories={subCategories}
        boxCategories={boxCategories}
        onSearchChange={setSearchQuery}
        setSearchQuery={setSearchQuery}
        setBoxCategories={setBoxCategories}
        setSubCategories={setSubCategories}
        selectedSubCategories={selectedSubCategories}
        setSelectedSubCategories={setSelectedSubCategories}
      />
      <BoxListCarousel
        hideThis={false}
        loading={loading}
        cardData={boxData}
        category={category}
        observerRef={observerRef}
        hasMore={pagination.hasMore}
        setShowOverlay={setShowOverlay}
      />
    </>
  );
};

export default UnBoxing;
